import { createFileRoute, redirect } from "@tanstack/react-router";
import { Activity, Flame } from "lucide-react";
import { ActivityByDay } from "../components/insights/activity-by-day";
import { CommunityEngagement } from "../components/insights/community-engagement";
import { SportTypeDistribution } from "../components/insights/sport-type-distribution";
import { TrendAnalysis } from "../components/insights/trend-analysis";
import { UserAchievementComparison } from "../components/insights/user-achievement-comparison";
import { MetricCard } from "../components/ui/metric-card";
import { useClubInsights } from "../data/clubs/hooks";
import { useAuthStore } from "../data/useAuthStore";

export const Route = createFileRoute("/insights")({
  component: RouteComponent,
  loader() {
    const activeClubId = useAuthStore.getState().activeClubId;

    if (!activeClubId) {
      return redirect({
        to: "/home",
      });
    }
  },
});

function RouteComponent() {
  const { data, isLoading, error } = useClubInsights();

  if (isLoading) {
    return (
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 mb-8">
        <div className="col-span-2 py-8 text-center text-muted-foreground">
          Loading...
        </div>
      </div>
    );
  }
  if (error) {
    return (
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 mb-8">
        <div className="col-span-2 py-8 text-center text-red-500">
          Error loading insights.
        </div>
      </div>
    );
  }

  if (!data) return null;

  return (
    <div className="bg-gradient-to-br from-[#2b7fff]/10 to-[#fe9a00]/10 w-full h-full">
      <div className="mb-4 p-4">
        <h1 className="text-3xl font-bold bg-gradient-to-r from-[#2b7fff] to-[#fe9a00] bg-clip-text text-transparent">
          Club Insights
        </h1>
        <p className="text-gray-500 text-sm md:text-base mb-1 sm:mb-0">
          Track employee wellness and fitness activities across your
          organization
        </p>
      </div>

      <div className="w-full px-4 sm:px-6 max-w-[1400px] mx-auto">
        <div className="grid grid-cols-1 lg:grid-cols-4 gap-4 mb-8">
          {/* First row with User Achievement Chart */}
          <div className="lg:col-span-2 grid grid-cols-1 gap-4">
            <MetricCard
              title="Activities"
              value={data.activitiesThisMonth}
              icon={<Activity className="h-5 w-5" />}
              description="Activities recorded this month"
              trend={{
                value: Number(data.activitiesTrend.value.toFixed(2)),
                positive: data.activitiesTrend.positive,
                timeWindow: data.activitiesTrend.timeWindow,
              }}
              variant="blue"
            />
            <MetricCard
              title="Calories Burned"
              value={data.thisMonthCaloriesBurned.toFixed(0)}
              icon={<Flame className="h-5 w-5" />}
              description="Calories burned this month"
              trend={{
                value: Number(data.caloriesBurnedTrend.value.toFixed(2)),
                positive: data.caloriesBurnedTrend.positive,
                timeWindow: data.caloriesBurnedTrend.timeWindow,
              }}
              variant="orange"
            />
          </div>

          {/* User Achievement Chart */}
          <div className="lg:col-span-2">
            <UserAchievementComparison />
          </div>
        </div>

        <TrendAnalysis />

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 mb-8">
          <SportTypeDistribution />
          <ActivityByDay />
        </div>

        <div className="mb-8">
          <CommunityEngagement />
        </div>

        {/* <div className="mb-8">
          <TeamPerformance />
        </div> */}
      </div>
    </div>
  );
}
